/** TitleBlockRegionEditor — draw the three title-block capture boxes on one
 *  sheet. Boxes are stored as fractional page rects (see titleBlockTemplate).
 *  Extraction reads the PDF text layer, so the backdrop only ever affects how
 *  easily a human can AIM the boxes — but that turned out to matter a lot: the
 *  backdrop used to be Drive's proxy thumbnail (a small page-1 preview
 *  stretched to full width), which left the title block an unreadable smear.
 *  It now prefers a real PDF.js render of the page actually being previewed,
 *  rasterised for the size the box occupies on screen (see pageRaster), and
 *  falls back to the proxy when that render is unavailable or fails — PDF.js
 *  is not dependable inside the macOS WKWebView shell, and a blurry backdrop
 *  beats no backdrop.
 *
 *  Pan/zoom lives entirely in this component (a single CSS transform on the
 *  content layer) so the pointer-drag draw path stays in one coordinate
 *  space: screen -> content (undo pan/zoom) -> fraction.
 *
 *  Layering: the root is its own stacking context (`isolate` + z-0) and clips
 *  its children, so the transformed backdrop can never paint over the floating
 *  app header or the footer actions the way it did before — same arrangement
 *  as the takeoff canvas, which sits on a z-0 layer with every piece of chrome
 *  floating above it. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Minus, Plus, Maximize2 } from '@/shared/ui/icons';
import type { TitleBlockTemplate, FractionalRect } from '../utils/titleBlockTemplate';
import { rectFromDrag, fractionalToPx } from './regionEditorMath';
import { ThumbnailImage } from '../../documents/ThumbnailImage';

export type RegionField = 'drawingNumber' | 'title' | 'revision';

export const REGION_FIELDS: { key: RegionField; label: string; color: string }[] = [
  { key: 'drawingNumber', label: 'Drawing No', color: 'var(--brand)' },
  { key: 'title', label: 'Title', color: '#2563eb' },
  { key: 'revision', label: 'Revision', color: '#16a34a' },
];

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;

/** Ceiling on how much viewport height the drawing may take. Without it a
 *  full-width A-series sheet is taller than the window on a laptop, so the
 *  review grid and the footer actions are pushed off the bottom — which is
 *  what "it's in front of everything" looked like. Applied as a MAX-WIDTH
 *  derived from the aspect ratio, never as a max-height: the box's ratio has
 *  to stay exactly equal to the page's or a box drawn over the visible title
 *  block maps to the wrong region of the page. */
const MAX_BACKDROP_VH = 48;

/** Extra detail baked into the backdrop raster so zooming in on the title
 *  block (up to MAX_ZOOM) still reads without re-rendering on every wheel
 *  tick. Bounded by MAX_RASTER_EDGE_PX inside rasterScaleForDisplay. Exported
 *  so the sizing policy stays owned by the component that displays it, even
 *  though TakeoffUploader owns the parsed document that does the rendering. */
export const BACKDROP_RASTER_QUALITY_FACTOR = 8;

export interface TitleBlockRegionEditorProps {
  /** Drive file id for the proxy-thumbnail fallback backdrop. */
  fileId: string;
  /** Source + 1-based page of the sheet being previewed. Multi-page sets are
   *  split into one sheet per page, all sharing a file, so the page is what
   *  distinguishes them — the proxy fallback can only ever show page 1, but
   *  the PDF render below honours it. */
  sourceId?: string;
  page: number;
  /** Rasterises `page` of `sourceId` to a data URL, sized for a box whose
   *  longest edge is `displayLongestEdgeCssPx`. Supplied by TakeoffUploader,
   *  which already holds the parsed document. Resolving null (or throwing)
   *  falls back to the Drive proxy thumbnail. */
  renderPage?: (sourceId: string, page: number, displayLongestEdgeCssPx: number) => Promise<string | null>;
  /** Reports which backdrop is actually on screen: the real PDF raster of
   *  `page`, or the Drive proxy (which can only ever show page 1). The caption
   *  above the editor must not claim "page N" while the proxy is showing page
   *  1 — the user would draw boxes against the wrong drawing. Fires 'proxy'
   *  first (the proxy shows while the async render is in flight and after any
   *  failure) and 'pdf' once the raster lands. */
  onBackdropChange?: (backdrop: 'pdf' | 'proxy') => void;
  template: TitleBlockTemplate;
  onChange(t: TitleBlockTemplate): void;
  // The real page's width/height ratio. Drawn box fractions are later applied
  // to the page's true baseW/baseH, so the container must match that ratio or
  // a box drawn over the visible (letterboxed) title block maps to the wrong
  // region on the page. Falls back to a landscape A-series guess when absent.
  aspectRatio?: number;
  // Which field a drawn box is assigned to — controlled by the parent
  // (TakeoffUploader) so the toggle lives in the global header instead of
  // above the canvas. Only read here (to colour the active/live rect); the
  // header drives it via takeoff:upload-field, so there's no local setter.
  activeField: RegionField;
}

export function TitleBlockRegionEditor({
  fileId, sourceId, page, renderPage, onBackdropChange, template, onChange, aspectRatio, activeField,
}: TitleBlockRegionEditorProps) {
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);

  // The PDF.js backdrop for the CURRENT (sourceId, page). Null while it is
  // being rendered, and after a failure — both cases fall through to the Drive
  // proxy thumbnail below, so the user is never left staring at an empty box.
  const [pdfBackdrop, setPdfBackdrop] = useState<string | null>(null);

  useEffect(() => {
    // The proxy is what shows until (and unless) the PDF raster lands, so the
    // parent's caption starts from that truth and is only upgraded to 'pdf'
    // once a real raster is in hand.
    setPdfBackdrop(null);
    onBackdropChange?.('proxy');
    if (!renderPage || !sourceId) return;
    let cancelled = false;
    // Measured, not assumed: the box is width-driven by its container and
    // capped by MAX_BACKDROP_VH, so only the laid-out element knows how many
    // CSS pixels the raster has to cover.
    const el = boxRef.current;
    const displayEdge = el ? Math.max(el.clientWidth, el.clientHeight) : 0;
    renderPage(sourceId, page, displayEdge)
      .then(url => {
        if (cancelled) return;
        setPdfBackdrop(url);
        onBackdropChange?.(url ? 'pdf' : 'proxy');
      })
      .catch(() => { /* proxy fallback renders instead; stays 'proxy' */ });
    return () => { cancelled = true; };
    // onBackdropChange is a stable setter from the parent; excluded so a new
    // closure each render can't re-fire the whole render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderPage, sourceId, page]);

  // Wheel must be a NON-passive native listener (React 19 registers onWheel
  // passively on its delegated root, so e.preventDefault() there is a no-op —
  // the browser's own pinch-zoom/scroll would still fire underneath). The
  // listener attaches once, so it reads live zoom/pan through refs instead of
  // closing over the state values from the render that attached it.
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panRef.current = pan; }, [pan]);

  const size = () => {
    const el = boxRef.current;
    return el ? { w: el.clientWidth, h: el.clientHeight } : { w: 1, h: 1 };
  };

  // Screen point -> content-space point (undoes the content layer's pan/zoom
  // transform) -> fraction of the base (zoom=1) size. Drawing must always
  // resolve to the same fractional rect regardless of how far zoomed in.
  const toContentPoint = (e: { clientX: number; clientY: number }) => {
    const r = boxRef.current!.getBoundingClientRect();
    const x = (e.clientX - r.left - pan.x) / zoom;
    const y = (e.clientY - r.top - pan.y) / zoom;
    return { x, y };
  };

  const onDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = toContentPoint(e);
    setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const p = toContentPoint(e);
    setDrag(d => (d ? { ...d, x1: p.x, y1: p.y } : d));
  };
  const onUp = () => {
    if (!drag) return;
    const { w, h } = size();
    const rect = rectFromDrag({ x: drag.x0, y: drag.y0 }, { x: drag.x1, y: drag.y1 }, w || 1, h || 1);
    setDrag(null);
    if (rect.w < 0.01 || rect.h < 0.01) return; // ignore stray clicks
    onChange({ ...template, [activeField]: rect });
  };

  // Wheel alone pans (trackpad two-finger scroll); wheel + ctrl/meta zooms
  // about the cursor — that's the gesture browsers send for a trackpad pinch.
  // preventDefault so the page never scrolls/zooms underneath the canvas —
  // only works because this is attached as a non-passive native listener below.
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const zoom = zoomRef.current;
    const pan = panRef.current;
    if (e.ctrlKey || e.metaKey) {
      const r = boxRef.current!.getBoundingClientRect();
      const cursor = { x: e.clientX - r.left, y: e.clientY - r.top };
      const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * (1 - e.deltaY * 0.01)));
      if (nextZoom === zoom) return;
      // Keep the point under the cursor stationary: solve for the new pan
      // that maps the same content point to the same screen point.
      const contentX = (cursor.x - pan.x) / zoom;
      const contentY = (cursor.y - pan.y) / zoom;
      setZoom(nextZoom);
      setPan({ x: cursor.x - contentX * nextZoom, y: cursor.y - contentY * nextZoom });
    } else {
      setPan({ x: pan.x - e.deltaX, y: pan.y - e.deltaY });
    }
  }, []);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const zoomBy = (factor: number) => {
    const { w, h } = size();
    const cursor = { x: w / 2, y: h / 2 };
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom * factor));
    if (nextZoom === zoom) return;
    const contentX = (cursor.x - pan.x) / zoom;
    const contentY = (cursor.y - pan.y) / zoom;
    setZoom(nextZoom);
    setPan({ x: cursor.x - contentX * nextZoom, y: cursor.y - contentY * nextZoom });
  };
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const { w, h } = size();
  const liveRect: FractionalRect | null = drag
    ? rectFromDrag({ x: drag.x0, y: drag.y0 }, { x: drag.x1, y: drag.y1 }, w || 1, h || 1)
    : null;

  const ratio = aspectRatio && Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1.414;

  return (
    <div ref={boxRef} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      className="relative isolate z-0 mx-auto w-full select-none touch-none rounded-lg overflow-hidden bg-muted/40 cursor-crosshair"
      style={{
        aspectRatio: `${ratio} / 1`,
        // Height cap expressed as a width cap so the ratio stays exact — see
        // MAX_BACKDROP_VH. height = width / ratio, so this pins height <= 48vh.
        maxWidth: `calc(${MAX_BACKDROP_VH}vh * ${ratio})`,
      }}>
      <div className="absolute inset-0" style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: '0 0' }}>
        {pdfBackdrop ? (
          <img src={pdfBackdrop} alt="" draggable={false} className="w-full h-full object-contain pointer-events-none" />
        ) : (
          <ThumbnailImage fileId={fileId} alt="" className="w-full h-full object-contain pointer-events-none" renderOnMacOS />
        )}
        {REGION_FIELDS.map(f => {
          const r = template[f.key];
          if (!r) return null;
          const px = fractionalToPx(r, w || 1, h || 1);
          return (
            <div key={f.key} className="absolute border-2 rounded-sm pointer-events-none"
              style={{ left: px.left, top: px.top, width: px.width, height: px.height, borderWidth: 2 / zoom, borderColor: f.color, background: `color-mix(in oklab, ${f.color} 8%, transparent)` }}>
              <span className="absolute top-0 left-0 whitespace-nowrap text-[10px] leading-4 font-semibold px-1 rounded" style={{ background: f.color, color: '#fff', transform: `scale(${1 / zoom}) translateY(-100%)`, transformOrigin: '0 0' }}>{f.label}</span>
            </div>
          );
        })}
        {liveRect && (
          <div className="absolute border-2 border-dashed pointer-events-none"
            style={{ ...fractionalToPx(liveRect, w || 1, h || 1), borderColor: REGION_FIELDS.find(f => f.key === activeField)!.color }} />
        )}
      </div>
      <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg bg-background/90 border border-border shadow-sm p-1 backdrop-blur-sm">
        <button type="button" onClick={() => zoomBy(1 / 1.4)} aria-label="Zoom out"
          className="h-7 w-7 flex items-center justify-center rounded-md text-foreground hover:bg-foreground/[0.06]">
          <Minus className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={resetView} aria-label="Fit to view"
          className="h-7 w-7 flex items-center justify-center rounded-md text-foreground hover:bg-foreground/[0.06]">
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => zoomBy(1.4)} aria-label="Zoom in"
          className="h-7 w-7 flex items-center justify-center rounded-md text-foreground hover:bg-foreground/[0.06]">
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
